import { ApiError } from './errors.js';
import type { HttpClient } from './http.js';

export interface UploadOptions {
  /** Upload progress between 0 and 1. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Multipart upload with progress. Uses XMLHttpRequest because fetch cannot report upload
 * progress. Retries once after refreshing the access token on 401.
 */
export async function uploadFile<T>(
  http: HttpClient,
  path: string,
  file: File,
  options: UploadOptions,
): Promise<T> {
  const attempt = () =>
    new Promise<{ status: number; body: unknown }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', http.url(path));
      const token = http.tokens.get()?.accessToken;
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) options.onProgress?.(event.loaded / event.total);
      };
      xhr.onload = () => {
        resolve({ status: xhr.status, body: parseJson(xhr.responseText) });
      };
      xhr.onerror = () => reject(new TypeError('Network error'));
      xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
      options.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      const form = new FormData();
      form.append('file', file, file.name);
      xhr.send(form);
    });

  let result = await attempt();
  if (result.status === 401 && (await http.refresh())) result = await attempt();
  if (result.status < 200 || result.status >= 300) throw new ApiError(result.status, result.body);
  return result.body as T;
}

function parseJson(text: string): unknown {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}
